import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

export interface PageLink {
  title: string;
  path: string;
  isActive: boolean;
}

export interface PageDataContextModel {
  moduleName?: string;
  setModuleName: (_moduleName: string) => void;
  pageTitle?: string;
  setPageTitle: (_title: string) => void;
  pageDescription?: string;
  setPageDescription: (_description: string) => void;
  pageBreadcrumbs?: Array<PageLink>;
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void;
  pageSubmenu?: Array<PageLink>;
  setPageSubmenu: (_submenu: Array<PageLink>) => void;
  businessScore?: number | null;
  setBusinessScore: (_score: number | null) => void;
}

const PageDataContext = createContext<PageDataContextModel>({
  setModuleName: (_moduleName: string) => {},
  setPageSubmenu: (_submenu: Array<PageLink>) => {},
  setPageTitle: (_title: string) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
  setBusinessScore: (_score: number | null) => {},
});

const PageDataProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [moduleName, setModuleNameState] = useState<string>("");
  const [pageTitle, setPageTitleState] = useState<string>("");
  const [pageDescription, setPageDescriptionState] = useState<string>("");
  const [pageBreadcrumbs, setPageBreadcrumbsState] = useState<Array<PageLink>>([]);
  const [pageSubmenu, setPageSubmenuState] = useState<Array<PageLink>>([]);
  const [businessScore, setBusinessScoreState] = useState<number | null>(null);

  const setModuleName = useCallback((name: string) => setModuleNameState(name), []);
  const setPageTitle = useCallback((title: string) => setPageTitleState(title), []);
  const setPageDescription = useCallback((desc: string) => setPageDescriptionState(desc), []);
  const setPageBreadcrumbs = useCallback((crumbs: Array<PageLink>) => setPageBreadcrumbsState(crumbs), []);
  const setPageSubmenu = useCallback((menu: Array<PageLink>) => setPageSubmenuState(menu), []);
  const setBusinessScore = useCallback((score: number | null) => setBusinessScoreState(score), []);

  const value: PageDataContextModel = {
    moduleName,
    setModuleName,
    pageTitle,
    setPageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
    pageSubmenu,
    setPageSubmenu,
    businessScore,
    setBusinessScore,
  };
  return (
    <PageDataContext.Provider value={value}>
      {children}
    </PageDataContext.Provider>
  );
};

function usePageData() {
  return useContext(PageDataContext);
}

type Props = {
  title?: string;
  description?: string;
  submenu?: Array<PageLink>;
  breadcrumbs?: Array<PageLink>;
};

const PageDataContainer: React.FC<Props & { children?: React.ReactNode }> = ({
  submenu,
  breadcrumbs,
  children,
}) => {
  const { setPageBreadcrumbs, setPageSubmenu } = usePageData();

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs);
    }

    if (submenu) {
      setPageSubmenu(submenu);
    }

    return () => {
      setPageBreadcrumbs([]);
      setPageSubmenu([]);
    };
  }, [breadcrumbs, submenu, setPageBreadcrumbs, setPageSubmenu]);
  return <>{children}</>;
};

const PageTitle: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { setPageTitle } = usePageData();
  useEffect(() => {
    if (children) {
      setPageTitle(children.toString());
    }
    return () => {
      setPageTitle("");
    };
  }, [children, setPageTitle]);
  return <></>;
};

const PageDescription: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { setPageDescription } = usePageData();
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString());
    }
    return () => {
      setPageDescription("");
    };
  }, [children, setPageDescription]);
  return <></>;
};


const ModuleName: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { setModuleName } = usePageData();
  useEffect(() => {
    if (children) {
      setModuleName(children.toString());
    }
    return () => {
      setModuleName("");
    };
  }, [children, setModuleName]);
  return <></>;
};

export {
  ModuleName,
  PageDescription,
  PageTitle,
  PageDataContainer,
  PageDataProvider,
  usePageData,
};
